import { useState } from "react";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AutocompleteInput } from "@/components/AutocompleteInput";
import { ParcelPhotosInput } from "@/components/ParcelPhotosInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function SendParcel() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [form, setForm] = useState({ origin: "", destination: "", date: "", departure_date: "", size: "m", weight: 1, description: "", price_offer: 0 });
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const scrollToError = () => setTimeout(() => {
    document.querySelector('[data-testid^="pack-error-"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 50);

  const Err = ({ k }) => (errors[k] ? <p className="text-xs text-destructive mt-1" data-testid={`pack-error-${k}`}>{errors[k]}</p> : null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const localToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    const todayStr = localToday();
    if (!form.origin.trim()) errs.origin = t("common.fieldRequired");
    if (!form.destination.trim()) errs.destination = t("common.fieldRequired");
    if (!form.description.trim()) errs.description = t("common.fieldRequired");
    if (!form.date) errs.date = t("common.fieldRequired");
    else if (form.date < todayStr) errs.date = t("parcel.deadlinePast");
    if (form.departure_date && form.departure_date < todayStr) errs.departure_date = t("parcel.departurePast");
    if (!errs.date && form.departure_date && form.date < form.departure_date) errs.date = t("parcel.deadlineBeforeDeparture");
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error(t("common.checkErrors"));
      scrollToError();
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/parcels", { ...form, weight: Number(form.weight), price_offer: Number(form.price_offer), photos });
      toast.success(t("parcel.success"));
      navigate(`/encomenda/${data.id}`);
    } catch (err) {
      const msg = formatApiError(err, t);
      toast.error(msg);
      setErrors({ form: msg });
      scrollToError();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="send-parcel-page">
      <h1 className="text-3xl md:text-4xl font-bold text-primary mb-8">{t("parcel.title")}</h1>
      <form onSubmit={submit} noValidate className="bg-white rounded-2xl border border-slate-100 p-6 md:p-10 shadow-sm space-y-6">
        <div className="grid gap-5 md:grid-cols-2">
          <div>
            <Label>{t("parcel.origin")} <span className="text-red-500">*</span></Label>
            <AutocompleteInput testid="parcel-origin-input" value={form.origin} onChange={(v) => { setForm({ ...form, origin: v }); setErrors((p) => ({ ...p, origin: undefined, form: undefined })); }} placeholder="Lausanne" className="mt-1.5" inputClassName={errors.origin ? "border-destructive h-12 rounded-xl" : "h-12 rounded-xl"} />
            <Err k="origin" />
          </div>
          <div>
            <Label>{t("parcel.destination")} <span className="text-red-500">*</span></Label>
            <AutocompleteInput testid="parcel-destination-input" value={form.destination} onChange={(v) => { setForm({ ...form, destination: v }); setErrors((p) => ({ ...p, destination: undefined, form: undefined })); }} placeholder="Porto" className="mt-1.5" inputClassName={errors.destination ? "border-destructive h-12 rounded-xl" : "h-12 rounded-xl"} />
            <Err k="destination" />
          </div>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          <div>
            <Label htmlFor="departure_date">{t("parcel.departureDate")} <span className="text-slate-400 font-normal text-xs">({t("common.optional")})</span></Label>
            <div className="relative">
              <Input id="departure_date" data-testid="parcel-departure-date-input" type="date" min={localToday()} value={form.departure_date} onChange={(e) => {
              const v = e.target.value;
              if (v && v < localToday()) {
                setErrors((p) => ({ ...p, departure_date: t("parcel.departurePast"), form: undefined }));
                return;
              }
              setForm({ ...form, departure_date: v });
              setErrors((p) => ({
                ...p,
                departure_date: undefined,
                form: undefined,
                date: form.date && v && form.date < v ? t("parcel.deadlineBeforeDeparture") : p.date,
              }));
            }} className={`mt-1.5 h-12 rounded-xl pr-9 ${errors.departure_date ? "border-destructive" : ""}`} />
              {form.departure_date && (
                <button
                  type="button"
                  data-testid="parcel-departure-date-clear"
                  title={t("parcel.clearDate")}
                  aria-label={t("parcel.clearDate")}
                  onClick={() => {
                    setForm({ ...form, departure_date: "" });
                    setErrors((p) => ({ ...p, departure_date: undefined, date: p.date === t("parcel.deadlineBeforeDeparture") ? undefined : p.date, form: undefined }));
                  }}
                  className="absolute right-3 top-[26px] -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors duration-150 z-10"
                >
                  <X size={15} />
                </button>
              )}
            </div>
            <Err k="departure_date" />
            <p className="text-xs text-slate-400 mt-1.5">{t("parcel.departureHint")}</p>
          </div>
          <div>
            <Label htmlFor="date">{t("parcel.date")} <span className="text-red-500">*</span></Label>
            <Input id="date" data-testid="parcel-date-input" type="date" required min={form.departure_date || localToday()} value={form.date} onChange={(e) => {
              const v = e.target.value;
              const minDate = form.departure_date || localToday();
              if (v && v < minDate) {
                setErrors((p) => ({ ...p, date: v < localToday() ? t("parcel.deadlinePast") : t("parcel.deadlineBeforeDeparture"), form: undefined }));
                return;
              }
              setForm({ ...form, date: v });
              setErrors((p) => ({ ...p, date: undefined, form: undefined }));
            }} className={`mt-1.5 h-12 rounded-xl ${errors.date ? "border-destructive" : ""}`} />
            <Err k="date" />
          </div>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          <div>
            <Label>{t("parcel.size")}</Label>
            <Select value={form.size} onValueChange={(v) => setForm({ ...form, size: v })}>
              <SelectTrigger data-testid="parcel-size-select" className="mt-1.5 h-12 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="s" data-testid="size-s">{t("parcel.sizeS")}</SelectItem>
                <SelectItem value="m" data-testid="size-m">{t("parcel.sizeM")}</SelectItem>
                <SelectItem value="l" data-testid="size-l">{t("parcel.sizeL")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="weight">{t("parcel.weight")}</Label>
            <Input id="weight" data-testid="parcel-weight-input" type="number" min="0" step="0.1" value={form.weight} onChange={set("weight")} className="mt-1.5 h-12 rounded-xl" />
          </div>
        </div>
        <div>
          <Label htmlFor="description">{t("parcel.description")} <span className="text-red-500">*</span></Label>
          <Textarea id="description" data-testid="parcel-description-input" required value={form.description} onChange={(e) => { set("description")(e); setErrors((p) => ({ ...p, description: undefined, form: undefined })); }} rows={3} className={`mt-1.5 rounded-xl ${errors.description ? "border-destructive" : ""}`} />
          <Err k="description" />
        </div>
        <div>
          <Label>{t("parcel.photos")} <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full ml-1">{t("parcel.photosRecommended")}</span></Label>
          <div className="mt-1.5"><ParcelPhotosInput photos={photos} onChange={setPhotos} testid="parcel-photos" /></div>
        </div>
        <div>
          <Label htmlFor="price">{t("parcel.priceOffer")}</Label>
          <Input id="price" data-testid="parcel-price-input" type="number" min="0" step="0.5" value={form.price_offer} onChange={set("price_offer")} className="mt-1.5 h-12 rounded-xl" />
        </div>
        {errors.form && <p className="text-sm text-destructive bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="pack-error-form">{errors.form}</p>}
        <Button data-testid="parcel-submit-button" type="submit" disabled={loading} className="w-full h-12 rounded-full bg-accent hover:bg-accent/90 text-white font-semibold">
          {t("parcel.submit")}
        </Button>
      </form>
    </div>
  );
}
